import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, BookOpen, Flame, Trophy } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Modal shown after a user completes a story.
 * Shows mystery box animation -> reveals reward item(s) with rarity.
 * Shows any newly unlocked badges below.
 */
const RARITY_META = {
  common:   { ring: '#a3a3a3', glow: 'rgba(163,163,163,0.4)', label: 'Gewoon'     },
  uncommon: { ring: '#6ea85f', glow: 'rgba(110,168,95,0.55)', label: 'Bijzonder' },
  rare:     { ring: '#3b82f6', glow: 'rgba(59,130,246,0.6)',  label: 'Zeldzaam'  },
  epic:     { ring: '#b76eff', glow: 'rgba(183,110,255,0.75)', label: 'Magisch'  },
};

const REASON_LABEL = {
  story: 'Verhaal uitgelezen',
  streak_3: '3 dagen streak!',
  streak_7: '7 dagen streak!',
  streak_30: '30 dagen streak!',
};

export default function GardenMysteryBox({ open, onClose, rewards = [], newBadges = [], blockedReason = null }) {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (open) setOpened(false);
  }, [open]);

  if (!open) return null;

  const empty = rewards.length === 0 && newBadges.length === 0;

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="mystery-box-modal"
      >
        <motion.div
          className="relative w-full max-w-md bg-card rounded-3xl shadow-2xl p-8 overflow-hidden"
          initial={{ scale: 0.7, y: 40, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: 'spring', damping: 18, stiffness: 220 }}
        >
          {/* sparkle BG */}
          <div className="absolute inset-0 pointer-events-none opacity-30">
            {Array.from({ length: 20 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-gold"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{ opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5] }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>

          <button
            data-testid="mystery-box-close"
            onClick={() => onClose()}
            className="absolute top-4 right-4 z-10 text-muted-foreground hover:text-foreground rounded-full p-1.5 hover:bg-muted transition-colors"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative text-center">
            {blockedReason ? (
              <BlockedState reason={blockedReason} onClose={onClose} />
            ) : empty ? (
              <EmptyState onClose={onClose} />
            ) : !opened ? (
              <ClosedBox onOpen={() => setOpened(true)} />
            ) : (
              <OpenedBox rewards={rewards} newBadges={newBadges} onClose={onClose} />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ClosedBox({ onOpen }) {
  return (
    <div className="py-4">
      <h2 className="font-display text-3xl text-foreground mb-2">Kijk eens!</h2>
      <p className="text-muted-foreground text-sm mb-6 font-accent">
        Omdat je het verhaal hebt uitgelezen ligt er iets voor je tuintje...
      </p>

      <motion.button
        data-testid="mystery-box-open-btn"
        onClick={onOpen}
        className="relative mx-auto mb-6 block"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={{ y: [0, -6, 0] }}
        transition={{ y: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } }}
      >
        <div
          className="w-40 h-40 rounded-3xl flex items-center justify-center text-7xl"
          style={{
            background: 'linear-gradient(135deg,#f9d48a 0%,#ffb85c 100%)',
            boxShadow: '0 10px 30px rgba(219,143,45,0.45), inset 0 -6px 12px rgba(0,0,0,0.1)',
          }}
        >
          🎁
        </div>
      </motion.button>

      <Button
        data-testid="mystery-box-reveal-btn"
        onClick={onOpen}
        size="lg"
        className="rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue font-bold px-8"
      >
        <Sparkles className="w-4 h-4 mr-2" /> Open je kadootje
      </Button>
    </div>
  );
}

function OpenedBox({ rewards, newBadges, onClose }) {
  return (
    <div className="py-2">
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 240, damping: 16 }}
        className="mb-5"
      >
        <h2 className="font-display text-3xl text-foreground">
          {rewards.length > 1 ? 'Kadootjes!' : 'Een nieuw kadootje!'}
        </h2>
      </motion.div>

      <div className="flex flex-wrap items-stretch justify-center gap-3 mb-5">
        {rewards.map((r, i) => {
          const meta = RARITY_META[r.rarity] || RARITY_META.common;
          return (
            <motion.div
              key={r.slug + i}
              initial={{ scale: 0, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.12, type: 'spring', stiffness: 220 }}
              className="relative rounded-2xl p-3 min-w-[8.5rem]"
              style={{
                border: `2px solid ${meta.ring}`,
                boxShadow: `0 0 22px ${meta.glow}`,
                background: 'rgba(255,255,255,0.5)',
              }}
              data-testid={`reward-item-${r.slug}`}
            >
              <div className="w-24 h-24 mx-auto mb-2">
                <img
                  src={`/garden-items/${r.slug}.png`}
                  alt={r.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-sm font-display text-foreground">{r.name}</div>
              <div className="text-[11px] font-accent mt-0.5" style={{ color: meta.ring }}>
                {meta.label}
              </div>
              {r.reason && REASON_LABEL[r.reason] && (
                <div className="text-[10px] mt-1 text-muted-foreground">
                  {REASON_LABEL[r.reason]}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {newBadges.length > 0 && (
        <div className="mb-5 py-3 border-t border-border">
          <div className="text-xs font-accent uppercase tracking-wide text-muted-foreground mb-2">
            Nieuwe badge{newBadges.length > 1 ? 's' : ''}!
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {newBadges.map((b) => (
              <motion.div
                key={b.slug}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 260 }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 text-gold border border-gold/30"
                data-testid={`badge-unlocked-${b.slug}`}
              >
                <BadgeIcon icon={b.icon} />
                <span className="text-xs font-bold">{b.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-2">
        <Button
          data-testid="mystery-box-to-garden"
          onClick={() => { onClose('/garden'); }}
          size="lg"
          className="rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue font-bold px-5"
        >
          Naar mijn tuintje
        </Button>
        <Button
          data-testid="mystery-box-stay"
          onClick={() => onClose()}
          variant="ghost"
          size="lg"
          className="rounded-full"
        >
          Later
        </Button>
      </div>
    </div>
  );
}

function EmptyState({ onClose }) {
  return (
    <div className="py-6">
      <div className="text-5xl mb-3">📖</div>
      <h2 className="font-display text-2xl text-foreground mb-2">Mooi gelezen!</h2>
      <p className="text-muted-foreground text-sm mb-2 font-accent leading-relaxed">
        Voor dit verhaal heb je <b>onlangs</b> al een kadootje gekregen.
      </p>
      <p className="text-muted-foreground text-xs mb-6 font-accent">
        Lees iets nieuws of probeer dit verhaal volgende week opnieuw voor weer een verrassing 🎁
      </p>
      <Button
        data-testid="mystery-box-ok"
        onClick={() => onClose()}
        size="lg"
        className="rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue font-bold px-8"
      >
        Oké
      </Button>
    </div>
  );
}

function BlockedState({ reason, onClose }) {
  const MESSAGES = {
    niet_uitgelezen: {
      emoji: '📖',
      title: 'Lees nog even verder...',
      text: 'Lees het verhaal helemaal tot de laatste pagina om je kadootje te verdienen!',
    },
    te_snel_doorgeklikt: {
      emoji: '🐢',
      title: 'Rustig aan!',
      text: 'Neem even de tijd om het verhaal te lezen. Kadootjes zijn er voor échte lezers — niet voor snelle klikkers.',
    },
    dagelijks_limiet_bereikt: {
      emoji: '🌙',
      title: 'Genoeg voor vandaag!',
      text: 'Je hebt vandaag al 5 kadootjes verdiend. Morgen liggen er weer nieuwe voor je klaar!',
    },
  };
  const m = MESSAGES[reason] || {
    emoji: '✨',
    title: 'Tot later!',
    text: 'Er ligt nu geen kadootje voor je klaar.',
  };
  return (
    <div className="py-6" data-testid={`mystery-box-blocked-${reason}`}>
      <div className="text-5xl mb-3">{m.emoji}</div>
      <h2 className="font-display text-2xl text-foreground mb-2">{m.title}</h2>
      <p className="text-muted-foreground text-sm mb-6 font-accent leading-relaxed px-2">
        {m.text}
      </p>
      <Button
        data-testid="mystery-box-blocked-ok"
        onClick={() => onClose()}
        size="lg"
        className="rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue font-bold px-8"
      >
        Oké
      </Button>
    </div>
  );
}

function BadgeIcon({ icon }) {
  const map = {
    'book-open': <BookOpen className="w-4 h-4" />,
    book: <BookOpen className="w-4 h-4" />,
    library: <BookOpen className="w-4 h-4" />,
    flame: <Flame className="w-4 h-4" />,
    trophy: <Trophy className="w-4 h-4" />,
    sparkles: <Sparkles className="w-4 h-4" />,
  };
  return map[icon] || <Sparkles className="w-4 h-4" />;
}
